import { ContentType } from "@/api/scode/scode_params";
import { usePosterSwipePro } from "@/components/poster-swiper/pro";
import { onShareAppMessageByRanking } from "@/utils/actions";
import { useShare } from "@/utils/share/share";
import { useEffect, useRef } from "react";

export interface RmsShareConfig {
  rmsShare: {
    contentType: ContentType; // 内容类型
    contentId: string; // 内容ID
    title?: string; // 分享标题
    marketName: string; // 市场名称
  };
  dependencies: any[];
}
export const useRmsShare = (config: RmsShareConfig) => {
  const { rmsShare, dependencies } = config;
  const { shareProduct, sharePageContent } = useShare();
  const productRef = useRef<any>(null);

  // 获取分享类型和海报代码
  const getShareTypeAndPosterCode = (contentType: ContentType) => {
    return {
      shareType: "COLLECT_MARKET",
      posterCode: "collectMarketPoster",
      src: "COLLECT_MARKET",
      pageName: "合集会场页",
      marketName: "合集会场页",
    };
  };

  // 会场分享控制器
  const swiperControl = usePosterSwipePro({
    defaultBizInfo: {
      // contentContext
      contentType: rmsShare.contentType,
      contentId: rmsShare.contentId || "",
      contentExtra: {
        posterCode: "collectMarketPoster",
      },
    },
    defaultEnvContext: {
      sourceScene: rmsShare.contentType,
      sourceSceneId: rmsShare.contentId || "",
      shareFrom: "MANUAL",
      scodeType: "SHARE",
      targetTerminalType: "H5",
    },
    defaultCarrierContext: {
      carrierElements: ["h5POSTER"],
      posterInfo: {
        posterCode: "collectMarketPoster",
      },
    },
    defaultUIConfig: {
      btnConfig: ["copy", "edit"],
    },
    onNonH5Open: options => {
      const { shareType, posterCode, src, pageName, marketName } = getShareTypeAndPosterCode(
        rmsShare.contentType,
      );

      // 确保contentId有值
      if (!rmsShare.contentId) {
        console.warn("分享失败：contentId为空");
        return;
      }

      sharePageContent({
        miniOptions: {
          contentId: rmsShare.contentId,
          contentType: rmsShare.contentType,
          pageName: marketName,
          shareType: shareType,
          posterCode: posterCode,
        },
        appOptions: {
          contentType: shareType,
          contentId: rmsShare.contentId,
          pageName: pageName,
          src: src,
          sid: rmsShare.contentId,
          shareType: shareType,
          posterCode: posterCode,
          imgType: posterCode,
        },
        appUrl: "forwardCollectMarket",
      });
    },
  });

  // 商品分享控制器
  const productControl = usePosterSwipePro({
    defaultBizInfo: {
      contentType: ContentType.spuProduct,
      contentId: "",
    },
    onNonH5Open: options => {
      if (!productRef.current) {
        return;
      }
      if (options.platform.isMini) {
        shareProduct({
          miniOptions: {
            ...productRef.current,
          },
        });
      } else if (options.platform.isApp) {
        const {
          activityId,
          activitySpuId,
          selectSkuId,
          spuId,
          skuExtendInfoVO = {},
        } = productRef.current;
        shareProduct({
          appOptions: {
            activityNo: activityId,
            productId: activitySpuId,
            selectSkuId: selectSkuId,
            spuId: spuId,
            src: "HOT_LIST_CATEGORY",
            sid: "",
            marketName: rmsShare.marketName,
            marketId: "",
            isOTC: !!skuExtendInfoVO?.isOTC,
          },
        });
      }
    },
  });

  // 更新分享配置
  useEffect(() => {
    // 确保contentId有值再更新配置
    if (!rmsShare.contentId) {
      console.warn("会场分享配置更新失败：contentId为空");
      return;
    }

    // 更新榜单分享配置
    swiperControl.updateConfig({
      bizInfo: {
        contentType: rmsShare.contentType,
        contentId: rmsShare.contentId,
      },
      envContext: {
        sourceScene: rmsShare.contentType,
        sourceSceneId: rmsShare.contentId,
      },
    });

    // 设置页面右上角分享
    if (rmsShare.contentId) {
      const { shareType, posterCode, marketName } = getShareTypeAndPosterCode(rmsShare.contentType);
      onShareAppMessageByRanking({
        contentType: rmsShare.contentType,
        contentId: rmsShare.contentId,
        title: rmsShare.title,
        shareType,
        posterCode,
        pageName: marketName,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  // 处理商品分享
  const handleShareProduct = (product: any) => {
    if (!product) {
      return;
    }
    productRef.current = product;
    // 打开分享海报
    productControl.open({
      bizInfo: {
        contentType: ContentType.spuProduct,
        contentId: product.activitySpuId,
        contentDetailId: product.selectSkuId || undefined,
      },
      uiConfig: {
        btnConfig: ["copy", "edit"],
      },
    });
  };

  return {
    swiperControl,
    productControl,
    handleShareProduct,
  };
};
